/** Alinhamento magnético ao arrastar blocos no preview (%, 0–100). */

export const SNAP_THRESHOLD_PCT = 1.25
export const GRID_SNAP_PCT = 5

export interface SnapGuide {
  orientation: 'vertical' | 'horizontal'
  position: number
}

export interface SnapResult {
  x: number
  y: number
  guides: SnapGuide[]
}

function snapAxis(
  value: number,
  targets: number[],
  orientation: SnapGuide['orientation'],
  threshold: number,
): { value: number; guides: SnapGuide[] } {
  const guides: SnapGuide[] = []
  let best = value
  let bestDist = threshold + 1

  for (const target of targets) {
    const dist = Math.abs(value - target)
    if (dist <= threshold && dist < bestDist) {
      bestDist = dist
      best = target
      guides.push({ orientation, position: target })
    }
  }

  return { value: best, guides }
}

function snapToGrid(value: number, grid: number): number {
  return Math.round(value / grid) * grid
}

export function snapDragPosition(params: {
  x: number
  y: number
  targetsX: number[]
  targetsY: number[]
  threshold?: number
  minX?: number
  maxX?: number
  minY?: number
  maxY?: number
  enableGrid?: boolean
}): SnapResult {
  const {
    x,
    y,
    targetsX,
    targetsY,
    threshold = SNAP_THRESHOLD_PCT,
    minX = 1,
    maxX = 99,
    minY = 1,
    maxY = 99,
    enableGrid = true,
  } = params

  let cx = Math.max(minX, Math.min(maxX, Math.round(x * 10) / 10))
  let cy = Math.max(minY, Math.min(maxY, Math.round(y * 10) / 10))

  const snapX = snapAxis(cx, targetsX, 'vertical', threshold)
  const snapY = snapAxis(cy, targetsY, 'horizontal', threshold)

  cx = snapX.value
  cy = snapY.value

  if (enableGrid && snapX.guides.length === 0) {
    const gridX = snapToGrid(cx, GRID_SNAP_PCT)
    if (Math.abs(gridX - cx) <= threshold * 0.6) cx = gridX
  }
  if (enableGrid && snapY.guides.length === 0) {
    const gridY = snapToGrid(cy, GRID_SNAP_PCT)
    if (Math.abs(gridY - cy) <= threshold * 0.6) cy = gridY
  }

  return {
    x: cx,
    y: cy,
    guides: [...snapX.guides, ...snapY.guides],
  }
}

export function snapDragY(params: {
  y: number
  targetsY: number[]
  threshold?: number
  minY?: number
  maxY?: number
}): SnapResult {
  const { y, targetsY, threshold = SNAP_THRESHOLD_PCT, minY = 1, maxY = 99 } = params
  let cy = Math.max(minY, Math.min(maxY, Math.round(y * 10) / 10))
  const snapY = snapAxis(cy, targetsY, 'horizontal', threshold)
  cy = snapY.value
  return { x: 0, y: cy, guides: snapY.guides }
}

export function uniqueTargets(values: number[], epsilon = 0.05): number[] {
  const sorted = [...values].sort((a, b) => a - b)
  const out: number[] = []
  for (const v of sorted) {
    if (!out.some((u) => Math.abs(u - v) < epsilon)) out.push(Math.round(v * 10) / 10)
  }
  return out
}
